import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiErrors";
import { useAuth } from "@/contexts/AuthContext";
import { applyAuthSession } from "@/lib/authSession";
import { toast } from "sonner";
import { identifyAnalyticsUser, trackEvent } from "@/lib/analytics";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [twofaCode, setTwofaCode] = useState("");
  const [submitting2fa, setSubmitting2fa] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        if (res.data?.requires_2fa && res.data?.challenge_token) {
          setChallengeToken(res.data.challenge_token);
          return;
        }
        // Store token for Authorization fallback (cookie is primary) and clear stale workspace.
        applyAuthSession(res.data);
        if (res.data?.user) {
          identifyAnalyticsUser(res.data.user);
          const marker = `factlify_registered_${res.data.user.user_id}`;
          if (!localStorage.getItem(marker)) {
            trackEvent("user_registered", {
              user_id: res.data.user.user_id,
              email: res.data.user.email || "",
            });
            localStorage.setItem(marker, "1");
          }
        }
        setUser(res.data?.user);
        const pendingInvite = sessionStorage.getItem("reforma_pending_invite");
        if (pendingInvite) {
          sessionStorage.removeItem("reforma_pending_invite");
          window.history.replaceState(null, "", `/accept-invite/${pendingInvite}`);
          navigate(`/accept-invite/${pendingInvite}`, { replace: true });
        } else {
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { replace: true, state: { user: res.data?.user } });
        }
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  const verifyTwofa = async () => {
    if (!challengeToken || !twofaCode.trim()) return;
    setSubmitting2fa(true);
    try {
      const res = await api.post("/auth/2fa/verify-login", {
        challenge_token: challengeToken,
        code: twofaCode.trim(),
      });
      applyAuthSession(res.data);
      if (res.data?.user) {
        identifyAnalyticsUser(res.data.user);
      }
      setUser(res.data?.user);
      const pendingInvite = sessionStorage.getItem("reforma_pending_invite");
      if (pendingInvite) {
        sessionStorage.removeItem("reforma_pending_invite");
        navigate(`/accept-invite/${pendingInvite}`, { replace: true });
      } else {
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: res.data?.user } });
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Código 2FA inválido"));
    } finally {
      setSubmitting2fa(false);
    }
  };

  if (challengeToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper p-4" data-testid="auth-callback-2fa">
        <div className="brutal-card p-6 w-full max-w-md space-y-3">
          <div className="overline">STEP · 02</div>
          <h2 className="font-display font-black text-2xl tracking-tight">Verificación 2FA</h2>
          <p className="text-sm text-steel">Introduce el código de 6 dígitos de tu app autenticadora.</p>
          <input
            data-testid="auth-2fa-code"
            value={twofaCode}
            onChange={(e) => setTwofaCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") verifyTwofa(); }}
            inputMode="numeric"
            autoFocus
            className="w-full bg-white border-2 border-slate-300 px-3 py-2 text-sm focus:border-blueprint focus:outline-none"
            placeholder="123456"
            maxLength={8}
          />
          <button
            data-testid="auth-2fa-submit"
            onClick={verifyTwofa}
            disabled={submitting2fa || !twofaCode.trim()}
            className="w-full bg-ink text-white border-2 border-ink font-bold uppercase tracking-wider px-5 py-2.5 disabled:opacity-50 hover:bg-blueprint hover:border-blueprint"
          >
            {submitting2fa ? "Verificando..." : "Verificar y entrar"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper" data-testid="auth-callback-loading">
      <div className="font-mono text-xs uppercase tracking-widest text-steel">Authenticating…</div>
    </div>
  );
}
